/**
 * Module-level store for comment thread visibility (internal / external).
 * Shared across React roots via useSyncExternalStore — state is always
 * replaced, never mutated, so reference equality checks work correctly.
 */

// Internal metadata (not part of React state)
const _annotationData = new Map(); // annotationId → { pageIndex: number, firstCommentId: string | null }
const _commentAnnotationMap = new Map(); // commentId → annotationId

// React-compatible state - immutable
let _state = {
  visibilityMap: new Map(), // annotationId → boolean (true = internal)
  postedAnnotationIds: new Set(), // annotationIds that have ≥1 comment (thread is "posted")
  firstCommentIds: new Set(), // commentIds of the first comment of each thread
};

const _listeners = new Set();
const _notify = () => _listeners.forEach((fn) => fn());

export const commentVisibilityStore = {
  subscribe(listener) {
    _listeners.add(listener);
    return () => _listeners.delete(listener);
  },
  getSnapshot() {
    return _state;
  },

  // Load a comment thread root annotation; skips non-roots. Defaults to internal.
  loadAnnotation(
    annotation,
    {markAsPosted = false, defaultInternal = true} = {}
  ) {
    if (!annotation.isCommentThreadRoot) return;

    const existing = _annotationData.get(annotation.id);
    _annotationData.set(annotation.id, {
      pageIndex: annotation.pageIndex,
      firstCommentId: existing?.firstCommentId ?? null,
    });

    // Determine visibility: explicit server value > existing store value > default
    const hasExplicitValue =
      annotation.customData?.isInternal === true ||
      annotation.customData?.isInternal === false;
    const existingValue = _state.visibilityMap.get(annotation.id);
    const isInternal = hasExplicitValue
      ? annotation.customData.isInternal === true
      : existingValue !== undefined
        ? existingValue
        : defaultInternal;

    const newVisMap = new Map(_state.visibilityMap);
    newVisMap.set(annotation.id, isInternal);

    const newPostedSet = new Set(_state.postedAnnotationIds);
    if (markAsPosted) {
      newPostedSet.add(annotation.id);
    }

    _state = {
      ..._state,
      visibilityMap: newVisMap,
      postedAnnotationIds: newPostedSet,
    };
    _notify();
  },

  // Optimistically update the visibility of a thread.
  setVisibility(annotationId, isInternal) {
    const newMap = new Map(_state.visibilityMap);
    newMap.set(annotationId, isInternal);
    _state = {..._state, visibilityMap: newMap};
    _notify();
  },

  // Returns { pageIndex } needed to fetch the annotation from Nutrient.
  getAnnotationMeta(annotationId) {
    return _annotationData.get(annotationId);
  },

  // Mark a thread as "posted" (has at least one comment).
  markAsPosted(annotationId) {
    if (_state.postedAnnotationIds.has(annotationId)) return;
    const newSet = new Set(_state.postedAnnotationIds);
    newSet.add(annotationId);
    _state = {..._state, postedAnnotationIds: newSet};
    _notify();
  },

  // Map a comment to its parent annotation. The first comment seen per
  // annotation is recorded as the thread's first comment (to display badge).
  mapComment(commentId, annotationId) {
    if (!commentId || !annotationId) return;
    _commentAnnotationMap.set(commentId, annotationId);

    // Only update when recording a new first comment
    const data = _annotationData.get(annotationId);
    if (data?.firstCommentId) return;
    _annotationData.set(annotationId, {...data, firstCommentId: commentId});

    const newFirstCommentIds = new Set(_state.firstCommentIds);
    newFirstCommentIds.add(commentId);
    _state = {..._state, firstCommentIds: newFirstCommentIds};
    _notify();
  },

  // When the first comment of a thread is deleted, promote a new comment as first.
  promoteFirstComment(oldCommentId, newCommentId) {
    const annotationId = _commentAnnotationMap.get(oldCommentId);
    if (!annotationId) return;

    _commentAnnotationMap.delete(oldCommentId);

    const data = _annotationData.get(annotationId);
    if (data) {
      _annotationData.set(annotationId, {
        ...data,
        firstCommentId: newCommentId ?? null,
      });
    }

    const newFirstCommentIds = new Set(_state.firstCommentIds);
    newFirstCommentIds.delete(oldCommentId);
    if (newCommentId) newFirstCommentIds.add(newCommentId);

    _state = {..._state, firstCommentIds: newFirstCommentIds};
    _notify();
  },

  // Returns the annotationId for a given commentId, or undefined.
  getAnnotationForComment(commentId) {
    return _commentAnnotationMap.get(commentId);
  },

  // Remove a deleted annotation and all its comment mappings from the store.
  deleteAnnotation(annotationId) {
    const firstCommentId = _annotationData.get(annotationId)?.firstCommentId;
    _annotationData.delete(annotationId);

    // Clean up all comments that belong to this thread
    for (const [commentId, annId] of _commentAnnotationMap) {
      if (annId === annotationId) _commentAnnotationMap.delete(commentId);
    }

    const newVisMap = new Map(_state.visibilityMap);
    newVisMap.delete(annotationId);
    const newPostedSet = new Set(_state.postedAnnotationIds);
    newPostedSet.delete(annotationId);
    const newFirstCommentIds = new Set(_state.firstCommentIds);
    if (firstCommentId) newFirstCommentIds.delete(firstCommentId);

    _state = {
      visibilityMap: newVisMap,
      postedAnnotationIds: newPostedSet,
      firstCommentIds: newFirstCommentIds,
    };
    _notify();
  },

  // Reset all state, called when nutrient viewer is unloaded
  reset() {
    _annotationData.clear();
    _commentAnnotationMap.clear();
    _state = {
      visibilityMap: new Map(),
      postedAnnotationIds: new Set(),
      firstCommentIds: new Set(),
    };
    _notify();
  },
};

// Slot components render in isolated React roots and can't use the app's Snackbar
// directly. PdfReviewPage registers a handler here to surface errors.
let _errorHandler = null;
export const setVisibilityErrorHandler = (handler) => {
  _errorHandler = handler;
};

// Optimistically update the store then persist the new isInternal value to
// Nutrient. On failure the store is reverted.
export const persistAnnotationVisibility = async (
  instance,
  annotationId,
  newIsInternal
) => {
  if (!instance) return;

  const prevIsInternal = _state.visibilityMap.get(annotationId) ?? true;

  // Optimistic update
  commentVisibilityStore.setVisibility(annotationId, newIsInternal);

  try {
    let annotation = null;

    // use stored pageIndex if available
    const meta = commentVisibilityStore.getAnnotationMeta(annotationId);
    if (meta?.pageIndex != null) {
      const pageAnnotations = await instance.getAnnotations(meta.pageIndex);
      annotation = pageAnnotations.find((a) => a.id === annotationId) ?? null;
    }

    // Fallback: scan all pages
    if (!annotation) {
      for (let i = 0; i < instance.totalPageCount; i++) {
        const pageAnnotations = await instance.getAnnotations(i);
        const match =
          pageAnnotations.find((a) => a.id === annotationId) ?? null;
        if (match) {
          annotation = match;
          commentVisibilityStore.loadAnnotation(match);
          break;
        }
      }
    }

    if (!annotation) {
      console.warn('[commentVisibility] Annotation not found:', annotationId);
      commentVisibilityStore.setVisibility(annotationId, prevIsInternal);
      _errorHandler?.('Failed to update comment visibility. Please try again.');
      return;
    }

    await instance.update(
      annotation.set('customData', {
        ...(annotation.customData ?? {}),
        isInternal: newIsInternal,
      })
    );
  } catch (err) {
    console.error('[commentVisibility] Failed to persist visibility:', err);
    commentVisibilityStore.setVisibility(annotationId, prevIsInternal);
    _errorHandler?.('Failed to update comment visibility. Please try again.');
    throw err;
  }
};
